import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  Slider,
} from "@mui/material";
import {
  ArrowBack,
  Security,
  CloudUpload,
  Delete,
  Edit,
  Add,
  Save,
  ExpandMore,
  Api,
  Email,
  Settings,
  CheckCircle,
  Warning,
  Info,
  ConnectWithoutContact,
  VpnKey,
  Fingerprint,
  AccountBalance,
  Description,
  Image,
  Upload,
  Download,
  Visibility,
  Refresh,
  History,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  loadPpSettings,
  savePpSettings,
} from "../../../../store/actions/ppActions";

const PpDigitalSignatureSetupPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const ppState = useSelector((state) => state[currentSession]?.pp);
  const settings = ppState?.settings?.data || {};

  // State management
  const [tabValue, setTabValue] = useState(0);
  const [signatureSettings, setSignatureSettings] = useState({
    // Digital Signature Configuration
    signatureMethod: "digital_certificate", // digital_certificate, biometric, pin, multi_factor
    certificateType: "professional", // professional, personal, enterprise
    certificateProvider: "digicert", // digicert, globalsign, sectigo, custom
    certificateDetails: {
      issuer: "",
      serialNumber: "",
      validFrom: "",
      validTo: "",
      status: "active",
    },

    // Authentication Methods
    authenticationMethods: {
      biometric: true,
      pin: false,
      sms: false,
      email: false,
      hardwareToken: false,
    },

    // Signature Templates
    signatureTemplates: [],
    defaultTemplate: null,

    // Stamp Configuration
    stampSettings: {
      enabled: true,
      uploadMethod: "upload", // upload, generate, template
      stamps: [],
      defaultStamp: null,
      autoApply: true,
      positioning: "bottom_right", // bottom_right, bottom_left, top_right, top_left
      opacity: 0.8,
      size: "medium", // small, medium, large, custom
      customSize: { width: 100, height: 100 },
    },

    // Workflow Configuration
    workflowSettings: {
      requireWitness: true,
      requireNotarySeal: true,
      autoTimestamp: true,
      auditTrail: true,
      complianceMode: "standard", // standard, enhanced, strict
      retentionPeriod: 7, // years
    },

    // Integration Settings
    integrationSettings: {
      apiEnabled: false,
      webhookUrl: "",
      autoSync: false,
      backupEnabled: true,
      encryptionLevel: "aes256", // aes128, aes256, rsa2048
    },
  });

  const [stampUploadOpen, setStampUploadOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingStamp, setGeneratingStamp] = useState(false);
  const [saving, setSaving] = useState(false);

  // Stamp preview and edit states
  const [stampPreviewOpen, setStampPreviewOpen] = useState(false);
  const [stampEditOpen, setStampEditOpen] = useState(false);
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [editingStamp, setEditingStamp] = useState(null);

  // Template creation form state
  const [templateForm, setTemplateForm] = useState({
    name: "",
    description: "",
    category: "general",
    layout: "standard",
    fields: [],
    stampSettings: {
      includeStamp: true,
      stampPosition: "bottom_right",
      stampSize: "medium",
      stampOpacity: 0.8,
    },
    signatureSettings: {
      includeSignature: true,
      signaturePosition: "bottom_left",
      signatureSize: "medium",
    },
    notarySettings: {
      includeNotaryInfo: true,
      includeDate: true,
      includeLocation: true,
      includeCommissionExpiry: false,
    },
    witnessSettings: {
      includeWitness: false,
      witnessCount: 1,
      witnessFields: ["name", "signature", "date"],
    },
    documentSettings: {
      includeDocumentInfo: false,
      includePropertyInfo: false,
      includeLegalDescription: false,
    },
    validationRules: {
      requireAllFields: true,
      requireWitnessSignature: false,
      requireNotarySeal: true,
    },
    styling: {
      fontFamily: "Arial",
      fontSize: 12,
      lineSpacing: 1.2,
      marginTop: 1,
      marginBottom: 1,
      marginLeft: 1,
      marginRight: 1,
    },
  });

  const [templateFormErrors, setTemplateFormErrors] = useState({});
  const [templateFormStep, setTemplateFormStep] = useState(0);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [renewCertificateOpen, setRenewCertificateOpen] = useState(false);
  const [certificateHistoryOpen, setCertificateHistoryOpen] = useState(false);
  const [certificateRenewing, setCertificateRenewing] = useState(false);
  const [stampsConfigExpanded, setStampsConfigExpanded] = useState(false);
  const [templateStampConfigExpanded, setTemplateStampConfigExpanded] =
    useState(true);

  // Mock data for stamps and templates
  const [stamps, setStamps] = useState([
    {
      id: "stamp_1",
      name: "Official Notary Seal",
      type: "seal",
      file: "/images/notary-seal.png",
      uploadedAt: new Date().toISOString(),
      status: "active",
      size: { width: 120, height: 120 },
    },
    {
      id: "stamp_2",
      name: "Professional Stamp",
      type: "signature",
      file: "/images/professional-stamp.png",
      uploadedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      status: "active",
      size: { width: 100, height: 50 },
    },
  ]);

  // Mock certificate history data
  const [certificateHistory] = useState([
    {
      id: "cert_1",
      action: "Certificate Issued",
      timestamp: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
      issuer: "DigiCert Inc.",
      serialNumber: "ABC123456789",
      status: "issued",
      validFrom: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
      validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "cert_2",
      action: "Certificate Renewed",
      timestamp: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
      issuer: "DigiCert Inc.",
      serialNumber: "DEF987654321",
      status: "renewed",
      validFrom: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
      validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "cert_3",
      action: "Certificate Updated",
      timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      issuer: "DigiCert Inc.",
      serialNumber: "GHI456789123",
      status: "updated",
      validFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      validTo: new Date(Date.now() + 335 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ]);

  const [templates, setTemplates] = useState([
    {
      id: "template_1",
      name: "Standard Notarization",
      description: "Basic notarization template with signature and seal",
      fields: ["signature", "seal", "date", "notary_info"],
      layout: "standard",
      createdAt: new Date().toISOString(),
    },
    {
      id: "template_2",
      name: "Real Estate Document",
      description: "Template for real estate notarization",
      fields: ["signature", "seal", "date", "property_info", "witness"],
      layout: "real_estate",
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ]);

  useEffect(() => {
    dispatch(loadPpSettings());
  }, [dispatch]);

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const updatedSettings = {
        ...settings,
        digitalSignature: signatureSettings,
      };
      await dispatch(savePpSettings(updatedSettings));
      // Show success message
    } catch (error) {
      console.error("Failed to save digital signature settings:", error);
      // Show error message
    } finally {
      setSaving(false);
    }
  };

  const handleStampUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      // Mock upload process
      setTimeout(() => {
        const newStamp = {
          id: `stamp_${Date.now()}`,
          name: file.name.replace(/\.[^/.]+$/, ""),
          type: "uploaded",
          file: URL.createObjectURL(file),
          uploadedAt: new Date().toISOString(),
          status: "active",
          size: { width: 100, height: 100 },
        };
        setStamps((prev) => [...prev, newStamp]);
        setUploading(false);
        setStampUploadOpen(false);
      }, 2000);
    }
  };

  const handleDeleteStamp = (stampId) => {
    setStamps((prev) => prev.filter((stamp) => stamp.id !== stampId));
  };

  const handlePreviewStamp = (stamp) => {
    setSelectedStamp(stamp);
    setStampPreviewOpen(true);
  };

  const handleEditStamp = (stamp) => {
    setEditingStamp(stamp);
    setStampEditOpen(true);
  };

  const handleUpdateStamp = (updatedStamp) => {
    setStamps((prev) =>
      prev.map((stamp) => (stamp.id === updatedStamp.id ? updatedStamp : stamp))
    );
    setStampEditOpen(false);
    setEditingStamp(null);
  };

  const handleGenerateStamp = async () => {
    try {
      setGeneratingStamp(true);

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Generate a new stamp with unique ID and timestamp
      const newStamp = {
        id: `stamp_${Date.now()}`,
        name: `Generated Stamp ${stamps.length + 1}`,
        type: "generated",
        file: "/images/generated-stamp.png", // Placeholder image
        uploadedAt: new Date().toISOString(),
        status: "active",
        size: { width: 120, height: 120 },
      };

      setStamps((prev) => [...prev, newStamp]);

      // Show success message or notification
    } catch (error) {
      console.error("Error generating stamp:", error);
    } finally {
      setGeneratingStamp(false);
    }
  };

  // Template creation functions
  const validateTemplateForm = () => {
    const errors = {};

    if (!templateForm.name.trim()) {
      errors.name = "Template name is required";
    }

    if (!templateForm.description.trim()) {
      errors.description = "Template description is required";
    }

    if (templateForm.fields.length === 0) {
      errors.fields = "At least one field must be selected";
    }

    setTemplateFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTemplateFieldToggle = (fieldType) => {
    setTemplateForm((prev) => ({
      ...prev,
      fields: prev.fields.includes(fieldType)
        ? prev.fields.filter((field) => field !== fieldType)
        : [...prev.fields, fieldType],
    }));
  };

  const handleTemplateSubmit = () => {
    if (validateTemplateForm()) {
      if (editingTemplate) {
        // Update existing template
        const updatedTemplate = {
          ...editingTemplate,
          name: templateForm.name,
          description: templateForm.description,
          category: templateForm.category,
          layout: templateForm.layout,
          fields: templateForm.fields,
          settings: {
            stamp: templateForm.stampSettings,
            signature: templateForm.signatureSettings,
            notary: templateForm.notarySettings,
            witness: templateForm.witnessSettings,
            document: templateForm.documentSettings,
            validation: templateForm.validationRules,
            styling: templateForm.styling,
          },
          updatedAt: new Date().toISOString(),
        };

        setTemplates((prev) =>
          prev.map((template) =>
            template.id === editingTemplate.id ? updatedTemplate : template
          )
        );
      } else {
        // Create new template
        const newTemplate = {
          id: `template_${Date.now()}`,
          name: templateForm.name,
          description: templateForm.description,
          category: templateForm.category,
          layout: templateForm.layout,
          fields: templateForm.fields,
          settings: {
            stamp: templateForm.stampSettings,
            signature: templateForm.signatureSettings,
            notary: templateForm.notarySettings,
            witness: templateForm.witnessSettings,
            document: templateForm.documentSettings,
            validation: templateForm.validationRules,
            styling: templateForm.styling,
          },
          createdAt: new Date().toISOString(),
          status: "active",
        };

        setTemplates((prev) => [...prev, newTemplate]);
      }

      setTemplateDialogOpen(false);
      resetTemplateForm();
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: "",
      description: "",
      category: "general",
      layout: "standard",
      fields: [],
      stampSettings: {
        includeStamp: true,
        stampPosition: "bottom_right",
        stampSize: "medium",
        stampOpacity: 0.8,
      },
      signatureSettings: {
        includeSignature: true,
        signaturePosition: "bottom_left",
        signatureSize: "medium",
      },
      notarySettings: {
        includeNotaryInfo: true,
        includeDate: true,
        includeLocation: true,
        includeCommissionExpiry: false,
      },
      witnessSettings: {
        includeWitness: false,
        witnessCount: 1,
        witnessFields: ["name", "signature", "date"],
      },
      documentSettings: {
        includeDocumentInfo: false,
        includePropertyInfo: false,
        includeLegalDescription: false,
      },
      validationRules: {
        requireAllFields: true,
        requireWitnessSignature: false,
        requireNotarySeal: true,
      },
      styling: {
        fontFamily: "Arial",
        fontSize: 12,
        lineSpacing: 1.2,
        marginTop: 1,
        marginBottom: 1,
        marginLeft: 1,
        marginRight: 1,
      },
    });
    setTemplateFormErrors({});
    setTemplateFormStep(0);
    setEditingTemplate(null);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);

    // Populate form with template data
    setTemplateForm({
      name: template.name,
      description: template.description,
      category: template.category,
      layout: template.layout,
      fields: template.fields,
      stampSettings: template.settings?.stamp || {
        includeStamp: true,
        stampPosition: "bottom_right",
        stampSize: "medium",
        stampOpacity: 0.8,
      },
      signatureSettings: template.settings?.signature || {
        includeSignature: true,
        signaturePosition: "bottom_left",
        signatureSize: "medium",
      },
      notarySettings: template.settings?.notary || {
        includeNotaryInfo: true,
        includeDate: true,
        includeLocation: true,
        includeCommissionExpiry: false,
      },
      witnessSettings: template.settings?.witness || {
        includeWitness: false,
        witnessCount: 1,
        witnessFields: ["name", "signature", "date"],
      },
      documentSettings: template.settings?.document || {
        includeDocumentInfo: false,
        includePropertyInfo: false,
        includeLegalDescription: false,
      },
      validationRules: template.settings?.validation || {
        requireAllFields: true,
        requireWitnessSignature: false,
        requireNotarySeal: true,
      },
      styling: template.settings?.styling || {
        fontFamily: "Arial",
        fontSize: 12,
        lineSpacing: 1.2,
        marginTop: 1,
        marginBottom: 1,
        marginLeft: 1,
        marginRight: 1,
      },
    });

    setTemplateFormErrors({});
    setTemplateFormStep(0);
    setTemplateDialogOpen(true);
  };

  const handleDeleteTemplate = (template) => {
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteTemplate = () => {
    if (templateToDelete) {
      setTemplates((prev) =>
        prev.filter((template) => template.id !== templateToDelete.id)
      );
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
    }
  };

  const cancelDeleteTemplate = () => {
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  // Certificate action functions
  const handleDownloadCertificate = () => {
    // Mock download functionality
    // In a real implementation, this would trigger a file download
  };

  const handleRenewCertificate = () => {
    setRenewCertificateOpen(true);
  };

  const handleViewCertificateHistory = () => {
    setCertificateHistoryOpen(true);
  };

  const confirmRenewCertificate = async () => {
    try {
      setCertificateRenewing(true);

      // Mock renewal process
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Update certificate details
      setSignatureSettings((prev) => ({
        ...prev,
        certificateDetails: {
          ...prev.certificateDetails,
          validFrom: new Date().toISOString(),
          validTo: new Date(
            Date.now() + 365 * 24 * 60 * 60 * 1000
          ).toISOString(),
          status: "active",
          serialNumber: `REN${Date.now()}`,
        },
      }));

      setRenewCertificateOpen(false);
      // Show success message
    } catch (error) {
      console.error("Failed to renew certificate:", error);
      // Show error message
    } finally {
      setCertificateRenewing(false);
    }
  };

  const cancelRenewCertificate = () => {
    setRenewCertificateOpen(false);
  };

  const closeCertificateHistory = () => {
    setCertificateHistoryOpen(false);
  };

  const getCertificateStatusIcon = (status) => {
    const iconMap = {
      issued: <CheckCircle color="success" />,
      renewed: <Refresh color="primary" />,
      updated: <Edit color="info" />,
      expired: <Warning color="warning" />,
      revoked: <Delete color="error" />,
    };
    return iconMap[status] || <Info />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const availableFields = [
    {
      id: "signature",
      label: "Digital Signature",
      description: "Digital signature field",
    },
    { id: "seal", label: "Notary Seal", description: "Official notary seal" },
    { id: "date", label: "Date", description: "Date field" },
    {
      id: "notary_info",
      label: "Notary Information",
      description: "Notary name and commission details",
    },
    {
      id: "witness",
      label: "Witness Signature",
      description: "Witness signature field",
    },
    {
      id: "property_info",
      label: "Property Information",
      description: "Property details for real estate",
    },
    {
      id: "legal_description",
      label: "Legal Description",
      description: "Legal property description",
    },
    {
      id: "document_info",
      label: "Document Information",
      description: "Document type and details",
    },
    {
      id: "commission_expiry",
      label: "Commission Expiry",
      description: "Notary commission expiry date",
    },
    { id: "location", label: "Location", description: "Notarization location" },
  ];

  const templateSteps = [
    { label: "Basic Information", icon: <Description /> },
    { label: "Field Selection", icon: <Edit /> },
    { label: "Layout & Styling", icon: <Settings /> },
    { label: "Preview", icon: <Visibility /> },
  ];

  const getStampTypeIcon = (type) => {
    const iconMap = {
      seal: <AccountBalance />,
      signature: <Edit />,
      uploaded: <CloudUpload />,
      template: <Description />,
      generated: <Add />,
    };
    return iconMap[type] || <Image />;
  };

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`signature-tabpanel-${index}`}
      aria-labelledby={`signature-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/settings")}>
          <ArrowBack />
        </IconButton>
        <Typography color="primary" variant="h4">
          Digital Signature Setup
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Configure your digital signature and stamp settings for secure
          notarization services. Manage certificates, authentication methods,
          signature templates, and official stamps.
        </Typography>
      </Alert>

      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
        >
          <Tab label="Digital Certificates" icon={<VpnKey />} />
          <Tab label="Signature Templates" icon={<Description />} />
          <Tab label="Stamps & Seals" icon={<AccountBalance />} />
          <Tab label="Authentication" icon={<Fingerprint />} />
          <Tab label="Workflow" icon={<Settings />} />
          <Tab label="Integration" icon={<Api />} />
        </Tabs>
      </Box>

      {/* Tab 1: Digital Certificates */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <VpnKey />
                  Certificate Configuration
                </Typography>

                <Grid container spacing={3} mt={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Signature Method</InputLabel>
                      <Select
                        value={signatureSettings.signatureMethod}
                        label="Signature Method"
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            signatureMethod: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="digital_certificate">
                          Digital Certificate
                        </MenuItem>
                        <MenuItem value="biometric">Biometric</MenuItem>
                        <MenuItem value="pin">PIN-based</MenuItem>
                        <MenuItem value="multi_factor">Multi-factor</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Certificate Type</InputLabel>
                      <Select
                        value={signatureSettings.certificateType}
                        label="Certificate Type"
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            certificateType: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="professional">Professional</MenuItem>
                        <MenuItem value="personal">Personal</MenuItem>
                        <MenuItem value="enterprise">Enterprise</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Certificate Provider</InputLabel>
                      <Select
                        value={signatureSettings.certificateProvider}
                        label="Certificate Provider"
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            certificateProvider: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="digicert">DigiCert</MenuItem>
                        <MenuItem value="globalsign">GlobalSign</MenuItem>
                        <MenuItem value="sectigo">Sectigo</MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Certificate Serial Number"
                      value={signatureSettings.certificateDetails.serialNumber}
                      onChange={(e) =>
                        setSignatureSettings((prev) => ({
                          ...prev,
                          certificateDetails: {
                            ...prev.certificateDetails,
                            serialNumber: e.target.value,
                          },
                        }))
                      }
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Certificate Status */}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <Typography variant="subtitle1">
                    Certificate Status
                  </Typography>
                  <Chip
                    icon={<CheckCircle />}
                    label="Active"
                    color="success"
                    size="small"
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Issued By"
                      value="DigiCert Inc."
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Valid Until"
                      value="12/31/2025"
                      disabled
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Certificate Actions
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Download />
                    </ListItemIcon>
                    <ListItemText primary="Download Certificate" />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleDownloadCertificate}
                      >
                        Download
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Refresh />
                    </ListItemIcon>
                    <ListItemText primary="Renew Certificate" />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleRenewCertificate}
                      >
                        Renew
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <History />
                    </ListItemIcon>
                    <ListItemText primary="View Certificate History" />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleViewCertificateHistory}
                      >
                        View
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 2: Signature Templates */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Description />
                    Signature Templates
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setTemplateDialogOpen(true)}
                  >
                    Create Template
                  </Button>
                </Box>

                <Grid container spacing={2}>
                  {templates.map((template) => (
                    <Grid key={template.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {template.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {template.description}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mb: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            {template.fields.map((field) => (
                              <Chip
                                key={field}
                                label={field.replace("_", " ")}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => handleEditTemplate(template)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleDeleteTemplate(template)}
                            >
                              Delete
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 3: Stamps & Seals */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <AccountBalance />
                    Official Stamps & Seals
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Upload />}
                      onClick={() => setStampUploadOpen(true)}
                    >
                      Upload Stamp
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={
                        generatingStamp ? <LinearProgress size={16} /> : <Add />
                      }
                      onClick={handleGenerateStamp}
                      disabled={generatingStamp}
                    >
                      {generatingStamp ? "Generating..." : "Generate Stamp"}
                    </Button>
                  </Box>
                </Box>

                {/* Stamp Settings */}
                <Accordion
                  expanded={stampsConfigExpanded}
                  onChange={(event, isExpanded) => {
                    if (event.defaultPrevented) return;
                    setStampsConfigExpanded(isExpanded);
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1">
                      Stamp Configuration
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={
                                signatureSettings.stampSettings.autoApply
                              }
                              onChange={(e) =>
                                setSignatureSettings((prev) => ({
                                  ...prev,
                                  stampSettings: {
                                    ...prev.stampSettings,
                                    autoApply: e.target.checked,
                                  },
                                }))
                              }
                            />
                          }
                          label="Auto-apply stamp to documents"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                          <InputLabel>Stamp Position</InputLabel>
                          <Select
                            value={signatureSettings.stampSettings.positioning}
                            label="Stamp Position"
                            onChange={(e) =>
                              setSignatureSettings((prev) => ({
                                ...prev,
                                stampSettings: {
                                  ...prev.stampSettings,
                                  positioning: e.target.value,
                                },
                              }))
                            }
                          >
                            <MenuItem value="bottom_right">
                              Bottom Right
                            </MenuItem>
                            <MenuItem value="bottom_left">Bottom Left</MenuItem>
                            <MenuItem value="top_right">Top Right</MenuItem>
                            <MenuItem value="top_left">Top Left</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid
                        size={{ xs: 12, sm: 6 }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <Typography gutterBottom>Stamp Opacity</Typography>
                        <Box
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <Slider
                            value={signatureSettings.stampSettings.opacity}
                            onChange={(e, newValue) =>
                              setSignatureSettings((prev) => ({
                                ...prev,
                                stampSettings: {
                                  ...prev.stampSettings,
                                  opacity: newValue,
                                },
                              }))
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            min={0.1}
                            max={1}
                            step={0.1}
                            marks={[
                              { value: 0.1, label: "10%" },
                              { value: 0.5, label: "50%" },
                              { value: 1, label: "100%" },
                            ]}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) =>
                              `${Math.round(value * 100)}%`
                            }
                            sx={{ mt: 2 }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Divider sx={{ my: 3 }} />

                {/* Stamps List */}
                <Grid container spacing={2}>
                  {stamps.map((stamp) => (
                    <Grid key={stamp.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            <Avatar
                              src={stamp.file}
                              variant="rounded"
                              sx={{ width: 40, height: 40 }}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6">{stamp.name}</Typography>
                              <Chip
                                icon={getStampTypeIcon(stamp.type)}
                                label={stamp.type}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Size: {stamp.size.width} × {stamp.size.height}px
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => handlePreviewStamp(stamp)}
                            >
                              Preview
                            </Button>
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => handleEditStamp(stamp)}
                            >
                              Edit
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteStamp(stamp.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 4: Authentication */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Fingerprint />
                  Authentication Methods
                </Typography>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Fingerprint />
                    </ListItemIcon>
                    <ListItemText
                      primary="Biometric Authentication"
                      secondary="Use fingerprint or face recognition for signing"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={
                          signatureSettings.authenticationMethods.biometric
                        }
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            authenticationMethods: {
                              ...prev.authenticationMethods,
                              biometric: e.target.checked,
                            },
                          }))
                        }
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <VpnKey />
                    </ListItemIcon>
                    <ListItemText
                      primary="PIN Authentication"
                      secondary="Use a personal identification number"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={signatureSettings.authenticationMethods.pin}
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            authenticationMethods: {
                              ...prev.authenticationMethods,
                              pin: e.target.checked,
                            },
                          }))
                        }
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <Email />
                    </ListItemIcon>
                    <ListItemText
                      primary="SMS Verification"
                      secondary="Receive SMS code for verification"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={signatureSettings.authenticationMethods.sms}
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            authenticationMethods: {
                              ...prev.authenticationMethods,
                              sms: e.target.checked,
                            },
                          }))
                        }
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <Email />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email Verification"
                      secondary="Receive email code for verification"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={signatureSettings.authenticationMethods.email}
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            authenticationMethods: {
                              ...prev.authenticationMethods,
                              email: e.target.checked,
                            },
                          }))
                        }
                      />
                    </ListItemSecondaryAction>
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <Security />
                    </ListItemIcon>
                    <ListItemText
                      primary="Hardware Token"
                      secondary="Use physical security token"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={
                          signatureSettings.authenticationMethods.hardwareToken
                        }
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            authenticationMethods: {
                              ...prev.authenticationMethods,
                              hardwareToken: e.target.checked,
                            },
                          }))
                        }
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Security Status
                </Typography>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Multi-factor authentication is enabled
                  </Typography>
                </Alert>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Certificate Valid" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Biometric Enabled" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="warning" />
                    </ListItemIcon>
                    <ListItemText primary="SMS Not Configured" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 5: Workflow */}
      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Settings />
                  Workflow Configuration
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            signatureSettings.workflowSettings.requireWitness
                          }
                          onChange={(e) =>
                            setSignatureSettings((prev) => ({
                              ...prev,
                              workflowSettings: {
                                ...prev.workflowSettings,
                                requireWitness: e.target.checked,
                              },
                            }))
                          }
                        />
                      }
                      label="Require Witness for Signatures"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            signatureSettings.workflowSettings.requireNotarySeal
                          }
                          onChange={(e) =>
                            setSignatureSettings((prev) => ({
                              ...prev,
                              workflowSettings: {
                                ...prev.workflowSettings,
                                requireNotarySeal: e.target.checked,
                              },
                            }))
                          }
                        />
                      }
                      label="Require Notary Seal"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            signatureSettings.workflowSettings.autoTimestamp
                          }
                          onChange={(e) =>
                            setSignatureSettings((prev) => ({
                              ...prev,
                              workflowSettings: {
                                ...prev.workflowSettings,
                                autoTimestamp: e.target.checked,
                              },
                            }))
                          }
                        />
                      }
                      label="Auto-timestamp Signatures"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            signatureSettings.workflowSettings.auditTrail
                          }
                          onChange={(e) =>
                            setSignatureSettings((prev) => ({
                              ...prev,
                              workflowSettings: {
                                ...prev.workflowSettings,
                                auditTrail: e.target.checked,
                              },
                            }))
                          }
                        />
                      }
                      label="Enable Audit Trail"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Compliance Mode</InputLabel>
                      <Select
                        value={
                          signatureSettings.workflowSettings.complianceMode
                        }
                        label="Compliance Mode"
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            workflowSettings: {
                              ...prev.workflowSettings,
                              complianceMode: e.target.value,
                            },
                          }))
                        }
                      >
                        <MenuItem value="standard">Standard</MenuItem>
                        <MenuItem value="enhanced">Enhanced</MenuItem>
                        <MenuItem value="strict">Strict</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Document Retention Period (years)"
                      value={signatureSettings.workflowSettings.retentionPeriod}
                      onChange={(e) =>
                        setSignatureSettings((prev) => ({
                          ...prev,
                          workflowSettings: {
                            ...prev.workflowSettings,
                            retentionPeriod: parseInt(e.target.value),
                          },
                        }))
                      }
                      inputProps={{ min: 1, max: 50 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 6: Integration */}
      <TabPanel value={tabValue} index={5}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Api />
                  Integration Settings
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            signatureSettings.integrationSettings.apiEnabled
                          }
                          onChange={(e) =>
                            setSignatureSettings((prev) => ({
                              ...prev,
                              integrationSettings: {
                                ...prev.integrationSettings,
                                apiEnabled: e.target.checked,
                              },
                            }))
                          }
                        />
                      }
                      label="Enable API Access"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            signatureSettings.integrationSettings.autoSync
                          }
                          onChange={(e) =>
                            setSignatureSettings((prev) => ({
                              ...prev,
                              integrationSettings: {
                                ...prev.integrationSettings,
                                autoSync: e.target.checked,
                              },
                            }))
                          }
                        />
                      }
                      label="Auto-sync with External Systems"
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Webhook URL"
                      value={signatureSettings.integrationSettings.webhookUrl}
                      onChange={(e) =>
                        setSignatureSettings((prev) => ({
                          ...prev,
                          integrationSettings: {
                            ...prev.integrationSettings,
                            webhookUrl: e.target.value,
                          },
                        }))
                      }
                      placeholder="https://your-system.com/webhook"
                      helperText="URL to receive signature completion notifications"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Encryption Level</InputLabel>
                      <Select
                        value={
                          signatureSettings.integrationSettings.encryptionLevel
                        }
                        label="Encryption Level"
                        onChange={(e) =>
                          setSignatureSettings((prev) => ({
                            ...prev,
                            integrationSettings: {
                              ...prev.integrationSettings,
                              encryptionLevel: e.target.value,
                            },
                          }))
                        }
                      >
                        <MenuItem value="aes128">AES-128</MenuItem>
                        <MenuItem value="aes256">AES-256</MenuItem>
                        <MenuItem value="rsa2048">RSA-2048</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            signatureSettings.integrationSettings.backupEnabled
                          }
                          onChange={(e) =>
                            setSignatureSettings((prev) => ({
                              ...prev,
                              integrationSettings: {
                                ...prev.integrationSettings,
                                backupEnabled: e.target.checked,
                              },
                            }))
                          }
                        />
                      }
                      label="Enable Backup"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Save Button */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Alert severity="info" sx={{ flexGrow: 1, mr: 2 }}>
              <Info sx={{ mr: 1 }} />
              Changes will be applied immediately and affect all future digital
              signatures.
            </Alert>
            <Button
              variant="contained"
              size="large"
              startIcon={<Save />}
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Stamp Upload Dialog */}
      <Dialog
        open={stampUploadOpen}
        onClose={() => setStampUploadOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Stamp</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CloudUpload
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" gutterBottom>
              Upload Your Official Stamp
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Supported formats: PNG, JPG, PDF. Maximum file size: 5MB.
            </Typography>
            <input
              accept="image/*,.pdf"
              style={{ display: "none" }}
              id="stamp-upload"
              type="file"
              onChange={handleStampUpload}
            />
            <label htmlFor="stamp-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={<Upload />}
              >
                Choose File
              </Button>
            </label>
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Uploading stamp...
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStampUploadOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Template Creation Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => {
          setTemplateDialogOpen(false);
          resetTemplateForm();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Description color="primary" />
            <Typography variant="h6">
              {editingTemplate
                ? "Edit Signature Template"
                : "Create Signature Template"}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Stepper */}
          <Box sx={{ px: 3, pt: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              {templateSteps.map((step, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor:
                        index <= templateFormStep ? "primary.main" : "grey.300",
                      color: index <= templateFormStep ? "white" : "grey.600",
                      mr: 1,
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        index <= templateFormStep ? "primary.main" : "grey.600",
                      fontWeight:
                        index === templateFormStep ? "bold" : "normal",
                    }}
                  >
                    {step.label}
                  </Typography>
                  {index < templateSteps.length - 1 && (
                    <Box
                      sx={{
                        width: 40,
                        height: 2,
                        backgroundColor:
                          index < templateFormStep
                            ? "primary.main"
                            : "grey.300",
                        mx: 2,
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Form Content */}
          <Box sx={{ p: 3 }}>
            {/* Step 1: Basic Information */}
            {templateFormStep === 0 && (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Template Name"
                    value={templateForm.name}
                    onChange={(e) =>
                      setTemplateForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    error={!!templateFormErrors.name}
                    helperText={templateFormErrors.name}
                    placeholder="e.g., Standard Notarization"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={templateForm.category}
                      label="Category"
                      onChange={(e) =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="real_estate">Real Estate</MenuItem>
                      <MenuItem value="legal">Legal Documents</MenuItem>
                      <MenuItem value="business">Business</MenuItem>
                      <MenuItem value="personal">Personal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={templateForm.description}
                    onChange={(e) =>
                      setTemplateForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    error={!!templateFormErrors.description}
                    helperText={
                      templateFormErrors.description ||
                      "Describe the purpose and use of this template"
                    }
                    multiline
                    rows={3}
                    placeholder="Describe when and how this template should be used..."
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Layout Type</InputLabel>
                    <Select
                      value={templateForm.layout}
                      label="Layout Type"
                      onChange={(e) =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          layout: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="real_estate">Real Estate</MenuItem>
                      <MenuItem value="legal">Legal Document</MenuItem>
                      <MenuItem value="compact">Compact</MenuItem>
                      <MenuItem value="detailed">Detailed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {/* Step 2: Field Selection */}
            {templateFormStep === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Select Fields to Include
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Choose which fields should be included in this signature
                  template
                </Typography>

                {templateFormErrors.fields && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {templateFormErrors.fields}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  {availableFields.map((field) => (
                    <Grid key={field.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: "pointer",
                          borderColor: templateForm.fields.includes(field.id)
                            ? "primary.main"
                            : "grey.300",
                          backgroundColor: templateForm.fields.includes(
                            field.id
                          )
                            ? "primary.50"
                            : "white",
                          "&:hover": {
                            borderColor: "primary.main",
                            backgroundColor: "primary.50",
                          },
                        }}
                        onClick={() => handleTemplateFieldToggle(field.id)}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Switch
                              checked={templateForm.fields.includes(field.id)}
                              onChange={() =>
                                handleTemplateFieldToggle(field.id)
                              }
                              color="primary"
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1">
                                {field.label}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {field.description}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Step 3: Layout & Styling */}
            {templateFormStep === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Layout & Styling Configuration
                </Typography>

                <Grid container spacing={3}>
                  {/* Stamp Settings */}
                  <Grid size={{ xs: 12 }}>
                    <Accordion
                      expanded={templateStampConfigExpanded}
                      onChange={(event, isExpanded) => {
                        if (event.defaultPrevented) return;
                        setTemplateStampConfigExpanded(isExpanded);
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          <AccountBalance
                            sx={{ mr: 1, verticalAlign: "middle" }}
                          />
                          Stamp Settings
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                      >
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    templateForm.stampSettings.includeStamp
                                  }
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      stampSettings: {
                                        ...prev.stampSettings,
                                        includeStamp: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="Include Notary Stamp"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                              <InputLabel>Stamp Position</InputLabel>
                              <Select
                                value={templateForm.stampSettings.stampPosition}
                                label="Stamp Position"
                                onChange={(e) =>
                                  setTemplateForm((prev) => ({
                                    ...prev,
                                    stampSettings: {
                                      ...prev.stampSettings,
                                      stampPosition: e.target.value,
                                    },
                                  }))
                                }
                              >
                                <MenuItem value="bottom_right">
                                  Bottom Right
                                </MenuItem>
                                <MenuItem value="bottom_left">
                                  Bottom Left
                                </MenuItem>
                                <MenuItem value="top_right">Top Right</MenuItem>
                                <MenuItem value="top_left">Top Left</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                              <InputLabel>Stamp Size</InputLabel>
                              <Select
                                value={templateForm.stampSettings.stampSize}
                                label="Stamp Size"
                                onChange={(e) =>
                                  setTemplateForm((prev) => ({
                                    ...prev,
                                    stampSettings: {
                                      ...prev.stampSettings,
                                      stampSize: e.target.value,
                                    },
                                  }))
                                }
                              >
                                <MenuItem value="small">Small</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="large">Large</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid
                            size={{ xs: 12, sm: 6 }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <Typography gutterBottom>Stamp Opacity</Typography>
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onTouchStart={(e) => e.stopPropagation()}
                              onPointerDown={(e) => e.stopPropagation()}
                            >
                              <Slider
                                value={templateForm.stampSettings.stampOpacity}
                                onChange={(e, newValue) =>
                                  setTemplateForm((prev) => ({
                                    ...prev,
                                    stampSettings: {
                                      ...prev.stampSettings,
                                      stampOpacity: newValue,
                                    },
                                  }))
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                                min={0.1}
                                max={1}
                                step={0.1}
                                marks={[
                                  { value: 0.1, label: "10%" },
                                  { value: 0.5, label: "50%" },
                                  { value: 1, label: "100%" },
                                ]}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(value) =>
                                  `${Math.round(value * 100)}%`
                                }
                                sx={{ mt: 2 }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  {/* Signature Settings */}
                  <Grid size={{ xs: 12 }}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          <Edit sx={{ mr: 1, verticalAlign: "middle" }} />
                          Signature Settings
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    templateForm.signatureSettings
                                      .includeSignature
                                  }
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      signatureSettings: {
                                        ...prev.signatureSettings,
                                        includeSignature: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="Include Digital Signature"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                              <InputLabel>Signature Position</InputLabel>
                              <Select
                                value={
                                  templateForm.signatureSettings
                                    .signaturePosition
                                }
                                label="Signature Position"
                                onChange={(e) =>
                                  setTemplateForm((prev) => ({
                                    ...prev,
                                    signatureSettings: {
                                      ...prev.signatureSettings,
                                      signaturePosition: e.target.value,
                                    },
                                  }))
                                }
                              >
                                <MenuItem value="bottom_left">
                                  Bottom Left
                                </MenuItem>
                                <MenuItem value="bottom_right">
                                  Bottom Right
                                </MenuItem>
                                <MenuItem value="top_right">Top Right</MenuItem>
                                <MenuItem value="top_left">Top Left</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  {/* Notary Settings */}
                  <Grid size={{ xs: 12 }}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          <Security sx={{ mr: 1, verticalAlign: "middle" }} />
                          Notary Information Settings
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    templateForm.notarySettings
                                      .includeNotaryInfo
                                  }
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      notarySettings: {
                                        ...prev.notarySettings,
                                        includeNotaryInfo: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="Include Notary Information"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    templateForm.notarySettings.includeDate
                                  }
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      notarySettings: {
                                        ...prev.notarySettings,
                                        includeDate: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="Include Date"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    templateForm.notarySettings.includeLocation
                                  }
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      notarySettings: {
                                        ...prev.notarySettings,
                                        includeLocation: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="Include Location"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    templateForm.notarySettings
                                      .includeCommissionExpiry
                                  }
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      notarySettings: {
                                        ...prev.notarySettings,
                                        includeCommissionExpiry:
                                          e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="Include Commission Expiry"
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  {/* Witness Settings */}
                  <Grid size={{ xs: 12 }}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          <ConnectWithoutContact
                            sx={{ mr: 1, verticalAlign: "middle" }}
                          />
                          Witness Settings
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={
                                    templateForm.witnessSettings.includeWitness
                                  }
                                  onChange={(e) =>
                                    setTemplateForm((prev) => ({
                                      ...prev,
                                      witnessSettings: {
                                        ...prev.witnessSettings,
                                        includeWitness: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label="Include Witness Signature"
                            />
                          </Grid>
                          {templateForm.witnessSettings.includeWitness && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField
                                fullWidth
                                type="number"
                                label="Number of Witnesses"
                                value={
                                  templateForm.witnessSettings.witnessCount
                                }
                                onChange={(e) =>
                                  setTemplateForm((prev) => ({
                                    ...prev,
                                    witnessSettings: {
                                      ...prev.witnessSettings,
                                      witnessCount: parseInt(e.target.value),
                                    },
                                  }))
                                }
                                inputProps={{ min: 1, max: 5 }}
                              />
                            </Grid>
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  {/* Styling Settings */}
                  <Grid size={{ xs: 12 }}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">
                          <Settings sx={{ mr: 1, verticalAlign: "middle" }} />
                          Styling & Formatting
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                              <InputLabel>Font Family</InputLabel>
                              <Select
                                value={templateForm.styling.fontFamily}
                                label="Font Family"
                                onChange={(e) =>
                                  setTemplateForm((prev) => ({
                                    ...prev,
                                    styling: {
                                      ...prev.styling,
                                      fontFamily: e.target.value,
                                    },
                                  }))
                                }
                              >
                                <MenuItem value="Arial">Arial</MenuItem>
                                <MenuItem value="Times New Roman">
                                  Times New Roman
                                </MenuItem>
                                <MenuItem value="Calibri">Calibri</MenuItem>
                                <MenuItem value="Georgia">Georgia</MenuItem>
                                <MenuItem value="Helvetica">Helvetica</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Font Size"
                              value={templateForm.styling.fontSize}
                              onChange={(e) =>
                                setTemplateForm((prev) => ({
                                  ...prev,
                                  styling: {
                                    ...prev.styling,
                                    fontSize: parseInt(e.target.value),
                                  },
                                }))
                              }
                              inputProps={{ min: 8, max: 24 }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Line Spacing"
                              value={templateForm.styling.lineSpacing}
                              onChange={(e) =>
                                setTemplateForm((prev) => ({
                                  ...prev,
                                  styling: {
                                    ...prev.styling,
                                    lineSpacing: parseFloat(e.target.value),
                                  },
                                }))
                              }
                              inputProps={{ min: 1, max: 2, step: 0.1 }}
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Step 4: Preview */}
            {templateFormStep === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Template Preview
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Review your template configuration before creating
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {templateForm.name || "Untitled Template"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {templateForm.description ||
                            "No description provided"}
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                          <Chip
                            label={templateForm.category}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={templateForm.layout}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={`${templateForm.fields.length} fields`}
                            size="small"
                          />
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                          Included Fields:
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 2,
                          }}
                        >
                          {templateForm.fields.map((field) => {
                            const fieldInfo = availableFields.find(
                              (f) => f.id === field
                            );
                            return (
                              <Chip
                                key={field}
                                label={fieldInfo?.label || field}
                                size="small"
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>
                          Configuration Summary:
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText
                              primary="Stamp Settings"
                              secondary={`Position: ${templateForm.stampSettings.stampPosition}, Size: ${templateForm.stampSettings.stampSize}, Opacity: ${Math.round(templateForm.stampSettings.stampOpacity * 100)}%`}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Signature Settings"
                              secondary={`Position: ${templateForm.signatureSettings.signaturePosition}, Size: ${templateForm.signatureSettings.signatureSize}`}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Styling"
                              secondary={`Font: ${templateForm.styling.fontFamily}, Size: ${templateForm.styling.fontSize}pt, Line Spacing: ${templateForm.styling.lineSpacing}`}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Validation Summary
                        </Typography>

                        <Alert
                          severity={
                            templateForm.name &&
                            templateForm.description &&
                            templateForm.fields.length > 0
                              ? "success"
                              : "warning"
                          }
                          sx={{ mb: 2 }}
                        >
                          {templateForm.name &&
                          templateForm.description &&
                          templateForm.fields.length > 0
                            ? "Template is ready to be created"
                            : "Please complete all required fields"}
                        </Alert>

                        <List dense>
                          <ListItem>
                            <ListItemIcon>
                              {templateForm.name ? (
                                <CheckCircle color="success" />
                              ) : (
                                <Warning color="warning" />
                              )}
                            </ListItemIcon>
                            <ListItemText primary="Template Name" />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              {templateForm.description ? (
                                <CheckCircle color="success" />
                              ) : (
                                <Warning color="warning" />
                              )}
                            </ListItemIcon>
                            <ListItemText primary="Description" />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              {templateForm.fields.length > 0 ? (
                                <CheckCircle color="success" />
                              ) : (
                                <Warning color="warning" />
                              )}
                            </ListItemIcon>
                            <ListItemText primary="Field Selection" />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button
              onClick={() => {
                setTemplateDialogOpen(false);
                resetTemplateForm();
              }}
            >
              Cancel
            </Button>

            <Box sx={{ display: "flex", gap: 1 }}>
              {templateFormStep > 0 && (
                <Button
                  onClick={() => setTemplateFormStep((prev) => prev - 1)}
                  disabled={templateFormStep === 0}
                >
                  Previous
                </Button>
              )}

              {templateFormStep < templateSteps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={() => setTemplateFormStep((prev) => prev + 1)}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleTemplateSubmit}
                  disabled={
                    !templateForm.name ||
                    !templateForm.description ||
                    templateForm.fields.length === 0
                  }
                >
                  {editingTemplate ? "Update Template" : "Create Template"}
                </Button>
              )}
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDeleteTemplate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Delete color="error" />
            <Typography variant="h6">Delete Template</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the template "
            {templateToDelete?.name}"?
          </Typography>
          <Alert severity="warning">
            <Typography variant="body2">
              This action cannot be undone. The template will be permanently
              removed from your account.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={cancelDeleteTemplate}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteTemplate}
            startIcon={<Delete />}
          >
            Delete Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renew Certificate Confirmation Dialog */}
      <Dialog
        open={renewCertificateOpen}
        onClose={cancelRenewCertificate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Refresh color="primary" />
            <Typography variant="h6">Renew Certificate</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to renew your digital certificate?
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Renewing your certificate will generate a new certificate with
              extended validity. The current certificate will remain valid until
              the renewal process is complete.
            </Typography>
          </Alert>

          <Box sx={{ p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Certificate Details:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Serial Number:{" "}
              {signatureSettings.certificateDetails.serialNumber ||
                "ABC123456789"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Valid Until:{" "}
              {signatureSettings.certificateDetails.validTo || "12/31/2025"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Status: {signatureSettings.certificateDetails.status || "Active"}
            </Typography>
          </Box>

          {certificateRenewing && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                Renewing certificate... This may take a few minutes.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={cancelRenewCertificate}
            disabled={certificateRenewing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmRenewCertificate}
            disabled={certificateRenewing}
            startIcon={<Refresh />}
          >
            {certificateRenewing ? "Renewing..." : "Renew Certificate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Certificate History Dialog */}
      <Dialog
        open={certificateHistoryOpen}
        onClose={closeCertificateHistory}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <History color="primary" />
            <Typography variant="h6">Certificate History</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            View the complete history of your digital certificate including
            issuance, renewals, and updates.
          </Typography>

          <List>
            {certificateHistory.map((entry, index) => (
              <ListItem
                key={entry.id}
                divider={index < certificateHistory.length - 1}
              >
                <ListItemIcon>
                  {getCertificateStatusIcon(entry.status)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="subtitle1">
                        {entry.action}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(entry.timestamp)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Serial Number: {entry.serialNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Issuer: {entry.issuer}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Valid From: {formatDate(entry.validFrom)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Valid Until: {formatDate(entry.validTo)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          {certificateHistory.length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <History sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No Certificate History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No certificate history records found.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeCertificateHistory}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Stamp Preview Dialog */}
      <Dialog
        open={stampPreviewOpen}
        onClose={() => setStampPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Visibility color="primary" />
            <Typography variant="h6">Stamp Preview</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedStamp && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedStamp.name}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  mb: 3,
                  backgroundColor: "#f5f5f5",
                }}
              >
                <Avatar
                  src={selectedStamp.file}
                  variant="rounded"
                  sx={{
                    width: selectedStamp.size.width,
                    height: selectedStamp.size.height,
                    fontSize: "2rem",
                  }}
                />
              </Box>
              <Grid container spacing={2} sx={{ textAlign: "left" }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Type:</strong> {selectedStamp.type}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Size:</strong> {selectedStamp.size.width} ×{" "}
                    {selectedStamp.size.height}px
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Status:</strong> {selectedStamp.status}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Created:</strong>{" "}
                    {new Date(selectedStamp.uploadedAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStampPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Stamp Edit Dialog */}
      <Dialog
        open={stampEditOpen}
        onClose={() => setStampEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Edit color="primary" />
            <Typography variant="h6">Edit Stamp</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingStamp && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Stamp Name"
                value={editingStamp.name}
                onChange={(e) =>
                  setEditingStamp({
                    ...editingStamp,
                    name: e.target.value,
                  })
                }
                sx={{ mb: 3 }}
              />
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={editingStamp.type}
                  onChange={(e) =>
                    setEditingStamp({
                      ...editingStamp,
                      type: e.target.value,
                    })
                  }
                  label="Type"
                >
                  <MenuItem value="seal">Seal</MenuItem>
                  <MenuItem value="signature">Signature</MenuItem>
                  <MenuItem value="uploaded">Uploaded</MenuItem>
                  <MenuItem value="generated">Generated</MenuItem>
                  <MenuItem value="template">Template</MenuItem>
                </Select>
              </FormControl>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Width (px)"
                    type="number"
                    value={editingStamp.size.width}
                    onChange={(e) =>
                      setEditingStamp({
                        ...editingStamp,
                        size: {
                          ...editingStamp.size,
                          width: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    label="Height (px)"
                    type="number"
                    value={editingStamp.size.height}
                    onChange={(e) =>
                      setEditingStamp({
                        ...editingStamp,
                        size: {
                          ...editingStamp.size,
                          height: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStampEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleUpdateStamp(editingStamp)}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpDigitalSignatureSetupPage;
